/* eslint-disable react/prop-types */
import PageHeader from 'components/common/PageHeader';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import messageHandler from 'utils/messageHandler';
import { useQuery } from 'utils/useQuery';
import {
  cleanForm,
  createProp,
  editBasicData,
  getPropById,
  getPropertiesForms,
  setPropActive,
  uploadCharacteristics,
  uploadImage
} from '../../../../../redux/propsSlice';
import PropertyWizardProvider from './PropertyWizardProvider';
import Steps from './Steps';
import { FooterWizard } from './footer/FooterWizard';
import { InfoWizard } from './info/InfoWizard';
import { NavWizard } from './nav/NavWizard';
import { getPageHeader } from './steps/data';

const PropertyWizard = ({ create }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const query = useQuery();
  const queryStep = Number(query.get('step'));

  const yupSubmitRef = useRef(null);

  const { form, types } = useSelector(state => state.props);

  const [step, setStep] = useState(queryStep || 1);
  const [action, setAction] = useState('');

  const [data, setData] = useState();
  const [stepStatus, setStepStatus] = useState();

  const fetchData = async () => {
    if (create) {
      setData({});
      setStepStatus({});
    } else {
      const propiedad = await dispatch(getPropById(id));

      setData(propiedad);
      setStepStatus(propiedad);
    }
  };

  const handleSubmit = async (values, noChanges) => {
    setData({ ...data, ...values });

    const response = !noChanges && (await handleData({ ...data, ...values }));

    if ((noChanges || response) && action === 'next' && step !== 5) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setStep(step + 1);
    } else if ((noChanges || response) && action === 'next' && step === 5) {
      await handleActivate();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if ((noChanges || response) && action === 'exit') {
      messageHandler('success', response?.data?.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('/propiedades');
    } else {
      messageHandler(
        'error',
        '¡Atención! Ocurrió un error al intentar guardar la información, por favor intente nuevamente.'
      );
    }
  };

  const handleData = async props => {
    const shareObj =
      // eslint-disable-next-line react/prop-types
      typeof props.share === 'string'
        ? // eslint-disable-next-line react/prop-types
          props.share
          ? {
              active: true,
              // eslint-disable-next-line react/prop-types
              percent: Number(props.share)
            }
          : {
              active: false,
              percent: 0
            }
        : // eslint-disable-next-line react/prop-types
          props.share;
    try {
      if (create) {
        if (step === 2) {
          const response = await handlePost({
            ...props,
            share: shareObj
          });

          if (response) {
            setData({
              ...data,
              ...props,
              _id: response,
              share: shareObj
            });
            return true;
          }
        }
        return true;
      }

      if (step !== 4) {
        // eslint-disable-next-line no-unused-vars, react/prop-types
        const { branch_office, real_estate, ...prop } = props;

        const response = await handlePatch({
          ...prop,
          // eslint-disable-next-line react/prop-types
          share: shareObj
        });

        setData({
          ...prop,
          // eslint-disable-next-line react/prop-types
          share: shareObj
        });

        return response;
      }

      const response = await handleVideos(props);
      setData(props);

      return response;
    } catch (e) {
      return false;
    }
  };

  const handlePost = async props => {
    const id = await dispatch(createProp(props));
    id && navigate(`/propiedades/editar/${id}?step=3`);
  };

  const handlePatch = async props => {
    switch (step) {
      case 1:
        return await patchStep1(props);
      case 2:
        return await patchStep2(props);
      case 3:
        return await patchStep3(props);
      case 5:
        return await patchStep5(props);
      default:
        break;
    }
  };

  const patchStep1 = async props => {
    const {
      branch_office_id,
      operation_type,
      property_type,
      sub_property_type,
      title,
      share
    } = props;

    const edicion = await dispatch(
      editBasicData(
        {
          branch_office_id,
          operation_type,
          property_type,
          sub_property_type,
          title,
          share
        },
        id
      )
    );
    return edicion;
  };

  const patchStep2 = async props => {
    const { location } = props;
    return await dispatch(editBasicData({ location }, id));
  };

  const patchStep3 = async props => {
    const {
      ambiences_types,
      antiquity,
      characteristics,
      description,
      price,
      status,
      surface
    } = props;

    return await dispatch(
      uploadCharacteristics(
        {
          ambiences_types,
          antiquity,
          characteristics,
          description,
          price,
          status,
          surface
        },
        id
      )
    );
  };

  const patchStep5 = async props => {
    const { extras } = props;
    return await dispatch(uploadCharacteristics({ extras }, id));
  };

  const handleVideos = async ({ _id, video, video360 }) => {
    let formData = new FormData();
    formData.append('video', video);
    formData.append('video360', video360);
    formData.append('imageType', 'ninguno');
    const response = await dispatch(uploadImage(_id, formData));
    return response;
  };

  const handleActivate = async () => {
    if (data.images < 3) {
      messageHandler(
        'error',
        '¡Atención! Debe cargar al menos 3 fotos para activar la propiedad.'
      );
      return;
    }

    const response = await dispatch(setPropActive(data._id));
    if (response < 400) {
      navigate('/propiedades');
      return response;
    }
  };

  const handleNavs = async targetStep => {
    if (!create) {
      window.scrollTo({ top: 50, behavior: 'smooth' });
      setStep(targetStep);
    }
  };

  useEffect(() => {
    if (data?.property_type) {
      dispatch(getPropertiesForms(data.property_type));
    }
  }, [data]);

  useEffect(() => {
    fetchData();

    return () => {
      dispatch(cleanForm());
    };
  }, [id, create]);

  return (
    <PropertyWizardProvider
      handleSubmit={handleSubmit}
      yupSubmitRef={yupSubmitRef}
      step={step}
      setStep={setStep}
      data={data}
      setData={setData}
      stepStatus={stepStatus}
      setStepStatus={setStepStatus}
      setAction={setAction}
      handleNavs={handleNavs}
      status={data?.status}
      form={form}
      types={types}
    >
      <PageHeader {...getPageHeader(data, create)} className="mb-3 bg-white" />

      <Card noValidate className="theme-wizard bg-white">
        <Card.Header className="pb-0">
          <NavWizard />
        </Card.Header>

        <hr className="text-300" />

        {data && (
          <Card.Body className="px-3 py-0 px-md-4">
            <Steps />

            <hr className="text-400" />

            <InfoWizard />
          </Card.Body>
        )}

        <hr className="text-300" />

        <Card.Footer className="d-flex justify-content-center justify-content-md-start gap-2 px-4 pt-0">
          <FooterWizard />
        </Card.Footer>
      </Card>
    </PropertyWizardProvider>
  );
};

PropertyWizard.propTypes = {
  create: PropTypes.bool
};

export default PropertyWizard;
